import EcdhKey from './ecdh';
import { getCookie } from './cookie';
import { getPresetKey } from './secure.config';
import { EUFY_SECURITY_APPLICATION_NAME } from './constants';
export interface RandomField {
  unisign: string
  keyindent: string
  once: string
  ts: string
}
export interface CustomHeaders {
  "Content-Type"?: string;
  "X-Replay-Info"?: string;
  "X-Encryption-Info"?: string;
  "X-Request-Ts"?: string;
  "X-Request-Once"?: string;
  "X-Signature"?: string;
  "X-Key-Ident"?: string;
  "App-Name"?: string;
  "Model-Type"?: string;
  "Gtoken"?:string;
}

async function HmacSHA256(key: string, message: string): Promise<string> {
  // 将字符串转换为Uint8Array
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  // 导入密钥
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: { name: 'SHA-256' } },
    false,
    ['sign']
  );

  // 签名消息
  const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, messageData);

  // 将签名转换为十六进制字符串
  const mac = Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  return mac;
}

export async function buildSignature(key: string, ts: string, once: string, body: string): Promise<string> {
  const message = body ? `${ts}+${once}+${body}` : `${ts}+${once}`;
  return HmacSHA256(key, message);
}

function generateRandomHex(size: number): string {
  const randomValues = window.crypto.getRandomValues(new Uint8Array(size));
  return Array.from(randomValues)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function buildUUidString(): string {
  // UUID version 4 without using uuid library
  const randomHex = generateRandomHex(16);
  return (
    randomHex.substring(0, 8) +
    '-' +
    randomHex.substring(8, 12) +
    '-4' + // UUID version 4
    randomHex.substring(13, 16) +
    '-' +
    ((parseInt(randomHex.substring(16, 18), 16) & 0x3f) | 0x80).toString(16) + // UUID variant 1
    randomHex.substring(18, 20) +
    '-' +
    randomHex.substring(20, 32)
  ).replace(/-/g, '');
}

export function buildField() {
  const unisign = buildUUidString()
  const keyindent = buildUUidString()
  const ts = String(Math.round(Date.now() / 1000))
  const once = buildUUidString()
  return { unisign, keyindent, ts, once }
}

export function buildBasicHeaders(): { randomField: RandomField; headers: CustomHeaders } {
  const randomField = buildField()
  const gToken = getCookie('gtoken')
  const headers = {
    "Content-Type": "application/json",
    "X-Replay-Info": "replay",
    "X-Encryption-Info": "algo_ecdh",
    "Model-Type": "PHONE", 
    "X-Request-Ts": randomField.ts,
    "X-Request-Once": randomField.once,
    "GToken":gToken
  }
  return { randomField, headers }
}

export async function buildExchangeKey(key: string, body: string, currenAppName?: string) {
  const { randomField, headers } = buildBasicHeaders()
  const signature = await buildSignature(key, randomField.ts, randomField.once, body)
  const appName = getCookie('app_name') || currenAppName || EUFY_SECURITY_APPLICATION_NAME
  headers["X-Signature"] = signature
  headers["X-Key-Ident"] = randomField.keyindent
  headers["App-Name"] = appName
  return { randomField, headers } 
}

const buildEdchKeyLocalKey = (currenAppName?: string) => {
  const appName = getCookie('app_name') || currenAppName  || EUFY_SECURITY_APPLICATION_NAME
  const key = btoa(`edch_key_${appName}`)
  return key
}

// 获取存储在本地的密钥，用于判断是否需要重新协议
export const getEcdhKeyLocalStorage = async (currenAppName?: string): Promise<any> => {
  try {
    const key = buildEdchKeyLocalKey(currenAppName)
    return key ? await getLocal(key, currenAppName) : ''
  } catch (e) {
    return ''
  }
}

// 拿到本地存储对其解密
export const getLocal = async (key: string, currenAppName?: string): Promise<unknown> => {
  let data: unknown
  const rawData = localStorage.getItem(key,)
  if (rawData) {
    const PRSET_KEY = getPresetKey(currenAppName)
    const decJson = await AesCBCDecrypt(rawData, PRSET_KEY);  
    data = JSON.parse(decJson)
  }
  return data
}

export const setEdchKeyLocal = async (ecdhKey: EcdhKey, currenAppName?: string) => {
  const key = await buildEdchKeyLocalKey()
  if (key) {
    localStorage.removeItem(key)
    await setLocal(key, ecdhKey, currenAppName)
  }
}

export async function setLocal(key: string, params: any, currenAppName?: string) {
  let newObj = params;
  if (typeof params === 'object') {
    newObj = { ...newObj };
  }
  const data = JSON.stringify(newObj);
  const PRSET_KEY = getPresetKey(currenAppName)
  let aesEncryptData = await AesCBCEncrypt(data, PRSET_KEY)
  localStorage.setItem(key, aesEncryptData);
}


async function encryptData(sourceData:any, keyBuffer:any, iv:any) {
  
  // 将源数据字符串转换为 ArrayBuffer
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(sourceData);

  // 导入密钥
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-CBC', length: 128 },
    false,
    ['encrypt']
  );

  // 加密数据
  const encryptedData = await window.crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: iv },
    cryptoKey,
    dataBuffer
  );

  return encryptedData;
}

/**
 * AES-CBC加密
 */
export async function AesCBCEncrypt(sourceData: string, key: string) {
  console.log('AesCBCEncrypt key===>', key);

  const blockSize = 16
  // 将十六进制密钥转换为 ArrayBuffer
  const keyBuffer = new Uint8Array(key.length / 2);
  for (let i = 0; i < key.length; i += 2) {
    keyBuffer[i / 2] = parseInt(key.substring(i, i + 2), 16);
  }

  // 检查密钥长度是否足够
  if (keyBuffer.length < blockSize) {
    console.error("AesCBCEncrypt: key too short");
    return sourceData;
  }

  // 为 AES-CBC 生成随机 IV
  const iv = window.crypto.getRandomValues(new Uint8Array(16));

  // 加密数据
  const encryptedData = await encryptData(sourceData, keyBuffer, iv);
  
  // 合并 IV 和密文
  const combinedData:any = new Uint8Array(iv.length + encryptedData.byteLength);
  combinedData.set(iv);
  combinedData.set(new Uint8Array(encryptedData), iv.length);
  
  // 将组合数据转换为 Base64
  const aesResultBase64 = btoa(String.fromCharCode.apply(null, combinedData));
  return aesResultBase64;
}

/**
 * AES-CBC解密
 */
export const AesCBCDecrypt = async (sourceData: string, key: string): Promise<string> => {
  if (!key) {
    return sourceData
  }
  const blockSize = 16;

  // 将十六进制密钥转换为 ArrayBuffer
  const keyBuffer = new Uint8Array(key.length / 2);
  for (let i = 0; i < key.length; i += 2) {
    keyBuffer[i / 2] = parseInt(key.substring(i, i + 2), 16);
  }

  // 检查密钥长度是否足够
  if (keyBuffer.length < blockSize) {
    console.error("AesCBCDecrypt: key too short");
    return sourceData;
  }

  // 将 Base64 编码数据转换为 ArrayBuffer
  const rawData = atob(sourceData);
  const rawBuffer = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; i++) {
    rawBuffer[i] = rawData.charCodeAt(i);
  }

  // 提取IV和加密数据
  const iv = rawBuffer.slice(0, blockSize);
  const encryptedData = rawBuffer.slice(blockSize);
  // 导入密钥
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-CBC', length: 128 },
    false,
    ['decrypt']
  );

  // 解密数据
  const decryptedData = await window.crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: iv },
    cryptoKey,
    encryptedData
  );
  // 将解密数据转换为 UTF-8 字符串
  const decoder = new TextDecoder();
  const decryptedStr = decoder.decode(decryptedData);
  return decryptedStr;
};
